import React, { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { AppContext } from "../context/app.context";
import { ModelCxContext } from "../context/modelCx.context";
import {
  MONGODB_AWSGW_QUERIES,
  MONGODB_AWSGW_APIKEY,
} from "../utility/keys";
import { resolveBackendMode } from "../utility/backendConfig";
import { refs } from "../convex/apiRefs";
import { convexReadEventLog } from "../convex/convexRead";
import { buildEventLogForCrewRequestBody } from "../utility/buildEventLogForCrewRequestBody";
import { parseEventLogResponse } from "../utility/parseEventLogResponse";
import { fetchAllLambdaChunks } from "../utility/fetchAllLambdaChunks";
import EventHistoryList from "./EventHistoryList";
import GridSpinner from "./GridSpinner";

/**
 * Time Machine #114 Phase 5 — combined History feed for the time-travel
 * crew modal. Fetches Candidates events + Attributes events for one
 * installer via the Lambda's server-side-merged `getEventLogForCrew`,
 * then renders via the shared <EventHistoryList> with kind tags enabled.
 *
 * Per the user's design decision: Attributes events are the load-bearing
 * data here (availability changes, OFF days, custom-note edits). Candidates
 * events are sparser informational rows. Both shown, tagged distinctly.
 *
 * Props:
 *   candidateId — ObjectId hex string of the Candidates document
 *   payrollNo   — payroll number string ("1345.01" format) — matches
 *                 attribute._kf_payrollNo
 *   onScrubbed  — optional fn() for double-click slider scrub
 */
const CrewHistorySection = ({ candidateId, payrollNo, onScrubbed }) => {
  const { appEnvirons } = useContext(AppContext) || {};
  const mongoDatabase = appEnvirons?.mongoDb || "Warehouses";
  // #191 Stage 3 — convexOnly reads TM history from Convex (PROD), not the Lambda.
  const { tsUserDocument } = useContext(ModelCxContext) || {};
  const isConvexOnly = resolveBackendMode(tsUserDocument) === "convexOnly";

  const isValidCandidateId =
    typeof candidateId === "string" && /^[0-9a-fA-F]{24}$/.test(candidateId);
  const isValidPayrollNo =
    typeof payrollNo === "string" && payrollNo.trim() !== "";
  const enabled = isValidCandidateId && isValidPayrollNo;

  const { data, isFetching, isError } = useQuery({
    queryKey: ["eventLogForCrew", candidateId, payrollNo, isConvexOnly ? "cx" : "lambda"],
    queryFn: async () => {
      const body = buildEventLogForCrewRequestBody({
        candidateId,
        payrollNo,
        database: mongoDatabase,
      });
      if (isConvexOnly) {
        return convexReadEventLog(
          refs.getEventLogForCrew,
          { candidateId, payrollNo },
          "getEventLogForCrew"
        );
      }
      // #163 — follow the Lambda chunk cursor so a long merged history is never
      // truncated by the 6 MB API Gateway cap.
      return fetchAllLambdaChunks(
        async (cursor) => {
          const response = await fetch(MONGODB_AWSGW_QUERIES, {
            method: "POST",
            mode: "cors",
            credentials: "omit",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              "x-api-key": MONGODB_AWSGW_APIKEY,
            },
            body: JSON.stringify(cursor ? { ...body, cursor } : body),
          });
          if (!response.ok) {
            throw new Error(
              `CrewHistorySection getEventLogForCrew HTTP ${response.status}`
            );
          }
          return response.json();
        },
        ["events"]
      );
    },
    select: (raw) => parseEventLogResponse(raw, "getEventLogForCrew"),
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  });

  return (
    <div
      data-testid="crew-history-section"
      className="mt-4 relative"
      style={{ minHeight: "120px" }}
    >
      <h3 className="font-bold text-base mb-2">History</h3>
      <GridSpinner show={isFetching} label="Loading history…" />
      {!enabled ? (
        <div className="text-sm italic text-skin-muted">
          {!isValidCandidateId ? "Missing crew identity." : "Missing payroll number."}
        </div>
      ) : isError ? (
        <div className="text-sm italic" style={{ color: "#ff6b6b" }}>
          Couldn't load history. Please contact I.T.
        </div>
      ) : data && data.ok === false ? (
        <div className="text-sm italic" style={{ color: "#ff6b6b" }}>
          {data.reason || "Unknown error loading history."}
          {data.lambdaVersion ? ` · Lambda ${data.lambdaVersion}` : ""}
        </div>
      ) : (
        <EventHistoryList
          events={(data && data.ok && data.events) || []}
          showKindTag={true}
          onScrubbed={onScrubbed}
        />
      )}
    </div>
  );
};

export default CrewHistorySection;
